import { arrowPosition } from '@/pure/floatingArrow';
import { arrow, autoUpdate, computePosition, flip, offset, Placement, shift } from '@floating-ui/dom';

const ARROW_ALIGN_PX = 12;
// Keeps the arrow off the panel's 6px rounded corners while still reaching an ⓘ ~20px from a phone's edge.
const ARROW_EDGE_PX = 4;
const GAP_PX = 6;

interface IPlaceOptions {
  // Points at the reference; it is a child of the floating element.
  arrow?: HTMLElement;
  edgePx?: number;
}

/**
 * Keeps a fixed-position floating element next to its reference, flipping and shifting to stay on screen, while
 * either moves. Returns the function that stops following.
 */
export default function placeFloating(
  reference: Element,
  floating: HTMLElement,
  placement: Placement,
  { arrow: arrowElement, edgePx = 8 }: IPlaceOptions = {},
) {
  const update = async () => {
    // A rotated square's tip is half its diagonal past its centre, which sits on the floating element's outer edge.
    const tipPx = arrowElement ? Math.ceil(arrowElement.offsetWidth * Math.SQRT1_2) : 0;
    const middleware = [
      offset(arrowElement ? { alignmentAxis: -ARROW_ALIGN_PX, mainAxis: GAP_PX + tipPx } : GAP_PX),
      flip(),
      shift({ padding: edgePx }),
    ];
    if (arrowElement) middleware.push(arrow({ element: arrowElement, padding: ARROW_EDGE_PX }));
    const {
      middlewareData,
      placement: finalPlacement,
      x,
      y,
    } = await computePosition(reference, floating, { middleware, placement, strategy: 'fixed' });
    Object.assign(floating.style, { left: `${x}px`, top: `${y}px` });
    if (!arrowElement) return;
    const outsetPx = arrowElement.offsetWidth / 2 + floating.clientLeft;
    const { side, style } = arrowPosition(finalPlacement, outsetPx, middlewareData.arrow?.x, middlewareData.arrow?.y);
    Object.assign(arrowElement.style, style);
    arrowElement.setAttribute('data-side', side);
  };
  return autoUpdate(reference, floating, update);
}
